import React, { useRef, useState } from 'react';
import { supabaseStorage } from '../services/supabase';
import { Upload, X, Check, Image as ImageIcon } from 'lucide-react';

interface MiniImageUploaderProps {
  onImageSelect: (url: string) => void;
  currentImage?: string;
}

const MiniImageUploader: React.FC<MiniImageUploaderProps> = ({ onImageSelect, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [recentUploads, setRecentUploads] = useState<string[]>(currentImage ? [currentImage] : []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      // Permitir subir múltiples archivos a la vez si el input lo permite (aunque aquí es uno por uno por defecto)
      // Pero procesamos el que viene
      const file = files[0];
      const fileName = `variant-${Date.now()}-${file.name}`;
      
      const result = await supabaseStorage.uploadImage(file, fileName);
      
      if (result.success) {
        setRecentUploads(prev => [...prev, result.publicUrl]);
        onImageSelect(result.publicUrl);
      } else {
        // TypeScript ahora sabe que si !success, result tiene la propiedad error
        alert('Error al subir imagen: ' + (result.error || 'Desconocido'));
      }
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploading(false);
      // Limpiar input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelect = (url: string) => {
    onImageSelect(url);
  };

  const handleRemove = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentUploads(prev => prev.filter(u => u !== url));
    if (currentImage === url) {
      onImageSelect('');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          <span>Subir Imagen</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
        <span className="text-xs text-gray-500">
          {recentUploads.length > 0 ? 'Selecciona una imagen de la lista:' : 'Sube una imagen para este tono'}
        </span>
      </div>

      {recentUploads.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
          {recentUploads.map((url, idx) => {
            const isSelected = currentImage === url;
            return (
              <div
                key={idx}
                onClick={() => handleSelect(url)}
                className={`relative group cursor-pointer w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                  isSelected ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-transparent hover:border-gray-300'
                }`}
              >
                <img src={url} alt="Variant" className="w-full h-full object-cover" />
                
                {isSelected && (
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <Check className="w-6 h-6 text-white drop-shadow-md" />
                  </div>
                )}

                <button
                  onClick={(e) => handleRemove(url, e)}
                  className="absolute top-0 right-0 p-0.5 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="Quitar de la lista"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MiniImageUploader;
